import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_TAG_COLOR = '#6b7280';
const VIEWPORT_PADDING = 8;
const LEGEND_GAP = 6;

const estimateLegendSize = (tags) => {
  const longestName = tags.reduce((max, tag) => Math.max(max, String(tag.name || '').length), 0);
  return {
    width: Math.max(120, Math.min(260, longestName * 7 + 48)),
    height: 16 + tags.length * 22,
  };
};

const TagLegend = ({ tags, onClick, style, refEl }) => (
  <div
    ref={refEl}
    className="fixed z-[1000] min-w-max rounded-md bg-gray-900 px-3 py-2 text-left shadow-lg ring-1 ring-black/10 dark:bg-gray-800"
    style={style}
    onClick={onClick}
    role="tooltip"
  >
    <div className="space-y-1">
      {tags.map(tag => (
        <div key={tag.id || tag.name} className="flex items-center gap-2 whitespace-nowrap text-xs text-white">
          <span
            className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-white/40"
            style={{ backgroundColor: tag.color || DEFAULT_TAG_COLOR }}
            aria-hidden="true"
          />
          <span>{tag.name}</span>
        </div>
      ))}
    </div>
  </div>
);

const CompactTagDots = ({ tags = [], maxVisible = 6 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [legendPosition, setLegendPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const legendRef = useRef(null);

  const closeLegend = () => {
    setIsOpen(false);
    setIsPinned(false);
  };

  const updateLegendPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const estimated = estimateLegendSize(tags);
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const actualWidth = legendRef.current?.offsetWidth || estimated.width;
    const actualHeight = legendRef.current?.offsetHeight || estimated.height;

    let top = rect.top - actualHeight - LEGEND_GAP;
    if (top < VIEWPORT_PADDING) {
      top = Math.min(rect.bottom + LEGEND_GAP, viewportHeight - actualHeight - VIEWPORT_PADDING);
    }
    top = Math.max(top, VIEWPORT_PADDING);

    let left = rect.left + rect.width / 2 - actualWidth / 2;
    left = Math.min(
      Math.max(left, VIEWPORT_PADDING),
      viewportWidth - actualWidth - VIEWPORT_PADDING
    );

    setLegendPosition({ top, left });
  }, [tags]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const withinTrigger = containerRef.current && containerRef.current.contains(event.target);
      const withinLegend = legendRef.current && legendRef.current.contains(event.target);
      if (withinTrigger || withinLegend) return;
      closeLegend();
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      closeLegend();
    };
    const handleViewportChange = () => {
      if (isOpen) updateLegendPosition();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange, true);
    };
  }, [isOpen, updateLegendPosition]);

  useEffect(() => {
    if (!isOpen) return;
    updateLegendPosition();
  }, [isOpen, tags, updateLegendPosition]);

  if (!tags || tags.length === 0) return null;

  const visibleTags = tags.slice(0, maxVisible);
  const hiddenCount = tags.length - visibleTags.length;
  const label = tags.map(tag => tag.name).join(', ');

  const openLegend = () => {
    updateLegendPosition();
    setIsOpen(true);
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const nextOpen = !isOpen || !isPinned;
    if (nextOpen) updateLegendPosition();
    setIsOpen(nextOpen);
    setIsPinned(nextOpen);
  };

  const handleMouseEnter = () => {
    if (!isPinned) openLegend();
  };

  const handleMouseLeave = () => {
    if (!isPinned) setIsOpen(false);
  };

  const handleBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    closeLegend();
  };

  return (
    <span
      ref={containerRef}
      className="relative mt-1 inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
    >
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex items-center rounded-full py-0.5 pr-1 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
        aria-label={`Tags: ${label}`}
        aria-expanded={isOpen}
        title={label}
        onClick={handleClick}
      >
        <span className="inline-flex items-center">
          {visibleTags.map((tag, index) => (
            <span
              key={tag.id || `${tag.name}-${index}`}
              className="inline-block h-2.5 w-2.5 rounded-full border border-white/80 shadow-sm dark:border-gray-900"
              style={{
                backgroundColor: tag.color || DEFAULT_TAG_COLOR,
                marginLeft: index === 0 ? 0 : -3,
                zIndex: visibleTags.length - index,
              }}
              aria-hidden="true"
            />
          ))}
        </span>
        {hiddenCount > 0 && (
          <span className="ml-1 text-[10px] leading-none text-gray-500 dark:text-gray-400">
            +{hiddenCount}
          </span>
        )}
      </button>
      {isOpen && createPortal(
        <TagLegend
          refEl={legendRef}
          tags={tags}
          style={legendPosition}
          onClick={(event) => event.stopPropagation()}
        />,
        document.body
      )}
    </span>
  );
};

export default CompactTagDots;
